import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, tap } from 'rxjs'
import { User } from '../../@core/interfaces/common/users'
import { UserStore } from '../../@core/stores/user.store'
import { MiddlemanService } from '../../auth/services/Middleman.service'
import { UserPreferences } from '../../models/userPreferences.model'
import { AccountViewState, PendingTransactionViewState, TransactionViewState, TransactionWithPendingAlertsViewState } from '../../models/viewState.model'
import { ThemeService } from './theme.service'
import { UserService } from './user.service'

// Account grid columns whose filter value is a real Date. Must stay in sync with
// the `filter: 'date'` entries in DataService.clientColumns - declared here
// rather than imported so this core service does not depend on the column
// catalogue.
const ACCOUNT_DATE_FILTER_FIELDS = new Set([
  'dateEntered',
  'modifiedDate',
  'approvedDate',
])

// Exactly what JSON.stringify emits for a Date, which is the only thing the
// localStorage round-trip needs revived. Deliberately not Date.parse: that
// accepts a bare digit as a year, so typing "1" into a text filter became
// Thu Mar 01 2001.
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

@Injectable({
  providedIn: 'root',
})
export class InitUserService {
  private rolesource = new BehaviorSubject<string>('')
  private branchsource = new BehaviorSubject<string>('')
  private userPreferences = new BehaviorSubject<UserPreferences>(null)
  private pendingTransactionViewState =
    new BehaviorSubject<PendingTransactionViewState>(null)
  private transactionViewState = new BehaviorSubject<TransactionViewState>(null)
  private transactionWithPendingAlertsViewState =
    new BehaviorSubject<TransactionWithPendingAlertsViewState>(null)
  private accountViewState = new BehaviorSubject<AccountViewState>(null)
  currentRole = this.rolesource.asObservable()
  currentBranchN = this.branchsource.asObservable()
  userPreferences$ = this.userPreferences.asObservable()
  pendingTransactionViewState$ = this.pendingTransactionViewState.asObservable()
  transactionViewState$ = this.transactionViewState.asObservable()
  transactionWithPendingAlertsViewState$ =
    this.transactionWithPendingAlertsViewState.asObservable()
  accountViewState$ = this.accountViewState.asObservable()

  constructor(
    protected userStore: UserStore,
    protected themeService: ThemeService,
    private http: HttpClient,
    private _nextclear: MiddlemanService,
    private userService: UserService,
  ) {
    this.setPendingTransactionViewState(
      localStorage.getItem('pendingTransactionViewState')
        ? JSON.parse(localStorage.getItem('pendingTransactionViewState'))
        : new PendingTransactionViewState(),
    )
    this.setTransactionViewState(
      localStorage.getItem('transactionViewState')
        ? JSON.parse(localStorage.getItem('transactionViewState'))
        : new TransactionViewState(),
    )
    this.setTransactionWithPendingAlertsViewState(
      localStorage.getItem('transactionWithPendingAlertsViewState')
        ? JSON.parse(
            localStorage.getItem('transactionWithPendingAlertsViewState'),
          )
        : new TransactionWithPendingAlertsViewState(),
    )
    this.setAccountViewState(
      localStorage.getItem('accountViewState')
        ? JSON.parse(localStorage.getItem('accountViewState'))
        : new AccountViewState(),
    )
    // Don't remove from localStorage - we want these to persist across refreshes
  }

  changeroles(roletext: string) {
    this.rolesource.next(roletext)
  }

  changebranchnumbers(branchnumbers: string) {
    this.branchsource.next(branchnumbers)
  }

  initCurrentUser(): Observable<User> {
    localStorage.removeItem('rpd')
    return this.http.get<any>(`/settings/current`).pipe(
      tap(
        (user: User) => {
          if (user) {
            localStorage.removeItem('theme')
            sessionStorage.removeItem('theme')

            localStorage.setItem('rpd', user.repId)
            this._nextclear.notifyClear(user.roleNames + '|' + user.roleId)
            this.changeroles(user.roleNames + '|' + user.roleId) // <ATP> Get roles like Session</ATP>
            this.changebranchnumbers(user.branchNumber)
            
            // Set theme and update localStorage
            const themeName = user.themeName ? user.themeName : 'light'
            this.themeService.theme = themeName
            localStorage.setItem('theme', themeName)
            
            // Dispatch custom event to notify the index.html script
            this.dispatchThemeChangeEvent(themeName)

            this.loadUserPreferences()
          }
        },
        () => {
          //  console.clear()
        },
      ),
    )
  }

  // Preferences used to be fetched only by the User Preferences page, so on a
  // fresh login this store held null and every grid silently fell back to its
  // own hardcoded default sort. Loading them as part of user init fixes that,
  // and seeding each grid's stored view state makes the saved sort actually
  // apply - the grids read the view state in preference to the raw setting.
  private loadUserPreferences(): void {
    this.userService.getUserPreferences().subscribe({
      next: (data) => {
        if (!data) {
          return
        }
        let prefs: UserPreferences
        try {
          // The UserPreferences constructor splits "Field dir" strings, so a
          // missing column would throw and take the whole login down with it.
          prefs = new UserPreferences(data)
        } catch {
          return
        }
        // Seed the view states first. The grids re-read the view state whenever
        // preferences emit, so if preferences went out first they would briefly
        // apply the old stored sort before settling.
        this.setAccountSortFromPreferences(
          prefs.clientSortField,
          prefs.clientSortDir,
        )
        this.setPendingSortFromPreferences(
          prefs.pendingSortField,
          prefs.pendingSortDir,
        )
        this.setTransactionSortFromPreferences(
          prefs.transactionSortField,
          prefs.transactionSortDir,
        )
        this.setUserPreferences(prefs)
      },
      // Never block sign-in over preferences; the grids keep their defaults.
      error: () => undefined,
    })
  }

  private dispatchThemeChangeEvent(theme: string): void {
    // Dispatch a custom event to notify the index.html script
    const event = new CustomEvent('themeChange', {
      detail: { theme: theme }
    })
    window.dispatchEvent(event)
  }

  setUserPreferences(userPreferences: UserPreferences) {
    this.userPreferences.next(userPreferences)
  }

  getPendingTransactionViewState() {
    return this.pendingTransactionViewState.getValue()
  }

  setPendingTransactionViewState(
    pendingTransactionViewState: PendingTransactionViewState,
  ) {
    this.pendingTransactionViewState.next(pendingTransactionViewState)
    // Persist to localStorage for page refreshes
    if (pendingTransactionViewState) {
      localStorage.setItem(
        'pendingTransactionViewState',
        JSON.stringify(pendingTransactionViewState),
      )
    } else {
      localStorage.removeItem('pendingTransactionViewState')
    }
  }

  // Clear only the grid filter on the pending view state, keeping the selected
  // view button (e.g. Document Review), branch, and sort. Used after a save
  // redirect so the pending screen loads without the single-transaction filter
  // but still remembers which view the user had selected.
  clearPendingTransactionFilter() {
    const current = this.getPendingTransactionViewState()
    this.setPendingTransactionViewState(
      new PendingTransactionViewState({
        ...(current || {}),
        filter: { logic: 'and', filters: [] },
      }),
    )
  }

  getTransactionViewState() {
    return this.transactionViewState.getValue()
  }

  setTransactionViewState(transactionViewState: TransactionViewState) {
    if (transactionViewState) {
      transactionViewState.approvalStartDate =
        transactionViewState.approvalStartDate
          ? new Date(transactionViewState.approvalStartDate)
          : null
      transactionViewState.approvalEndDate =
        transactionViewState.approvalEndDate
          ? new Date(transactionViewState.approvalEndDate)
          : null
      transactionViewState.dateReceivedStartDate =
        transactionViewState.dateReceivedStartDate
          ? new Date(transactionViewState.dateReceivedStartDate)
          : null
      transactionViewState.dateReceivedEndDate =
        transactionViewState.dateReceivedEndDate
          ? new Date(transactionViewState.dateReceivedEndDate)
          : null
    }

    this.transactionViewState.next(transactionViewState)
    // Persist to localStorage for page refreshes
    if (transactionViewState) {
      localStorage.setItem(
        'transactionViewState',
        JSON.stringify(transactionViewState),
      )
    } else {
      localStorage.removeItem('transactionViewState')
    }
  }

  getTransactionWithPendingAlertsViewState() {
    return this.transactionWithPendingAlertsViewState.getValue()
  }

  setTransactionWithPendingAlertsViewState(
    viewState: TransactionWithPendingAlertsViewState,
  ) {
    if (viewState) {
      viewState.submittedStartDate = viewState.submittedStartDate
        ? new Date(viewState.submittedStartDate)
        : null
      viewState.submittedEndDate = viewState.submittedEndDate
        ? new Date(viewState.submittedEndDate)
        : null
    }

    this.transactionWithPendingAlertsViewState.next(viewState)
    // Persist to localStorage for page refreshes
    if (viewState) {
      localStorage.setItem(
        'transactionWithPendingAlertsViewState',
        JSON.stringify(viewState),
      )
    } else {
      localStorage.removeItem('transactionWithPendingAlertsViewState')
    }
  }

  getAccountViewState() {
    return this.accountViewState.getValue()
  }

  setAccountViewState(accountViewState: AccountViewState) {
    if (accountViewState?.filter) {
      // Rebuild rather than mutate: the caller hands us the grid's live filter
      // object (viewaccounts dataStateChange passes state.filter by reference),
      // and writing to it reaches back into the rendered filter cells.
      accountViewState = {
        ...accountViewState,
        filter: this.reviveFilterDates(accountViewState.filter),
      }
    }

    this.accountViewState.next(accountViewState)
    // Persist to localStorage for page refreshes
    if (accountViewState) {
      localStorage.setItem(
        'accountViewState',
        JSON.stringify(accountViewState),
      )
    } else {
      localStorage.removeItem('accountViewState')
    }
  }

  // Returns a new descriptor tree with the date columns' ISO strings turned back
  // into Dates. Descriptors for non-date columns that hold an ISO string are
  // dropped: nobody types a timestamp into Account Name, so such a value can
  // only be state an earlier build corrupted, and discarding it clears the
  // filter box on the next load instead of showing a date stamp.
  private reviveFilterDates(filter: any): any {
    if (!filter?.filters) {
      return filter
    }

    return {
      ...filter,
      filters: filter.filters
        .map((descriptor: any) => {
          if (descriptor?.filters) {
            return this.reviveFilterDates(descriptor)
          }

          const looksLikeDate =
            typeof descriptor?.value === 'string' &&
            ISO_DATE_TIME.test(descriptor.value)

          if (!looksLikeDate) {
            return { ...descriptor }
          }

          return ACCOUNT_DATE_FILTER_FIELDS.has(descriptor.field)
            ? { ...descriptor, value: new Date(descriptor.value) }
            : null
        })
        .filter((descriptor: any) => descriptor !== null),
    }
  }

  // A newly saved Account Screen sort preference has to be pushed into the
  // stored account view state as well. That state's sort is always populated
  // (it defaults to modifiedDate desc), so on the next load it would otherwise
  // overwrite the preference the user just chose. The grid's own sort changes
  // keep persisting through setAccountViewState() as before.
  setAccountSortFromPreferences(field: string, dir: 'asc' | 'desc') {
    if (!field || !dir) {
      return
    }
    const current = this.getAccountViewState()
    this.setAccountViewState(
      new AccountViewState({
        ...(current || {}),
        sort: [{ field, dir }],
      }),
    )
  }

  // Same problem as the account sort above: PendingTransactionViewState.sort is
  // always populated (defaulting to dateReceived desc), so pending.component's
  //   this.sort = viewState?.sort.length ? viewState.sort : this.sort
  // always took the view state and discarded the saved preference.
  setPendingSortFromPreferences(field: string, dir: 'asc' | 'desc') {
    if (!field || !dir) {
      return
    }
    const current = this.getPendingTransactionViewState()
    this.setPendingTransactionViewState(
      new PendingTransactionViewState({
        ...(current || {}),
        sort: [{ field, dir }],
      }),
    )
  }

  // Same override as the two above, on transaction-list.component:
  //   if (viewState.sort && viewState.sort.length > 0) this.sort = viewState.sort
  // TransactionViewState.sort always defaults to dateSubmitted desc, so that
  // guard never failed and transactionSortField never took effect.
  //
  // Deliberately spread rather than `new TransactionViewState(...)`: that
  // constructor reads data.selectedRep for selectedRepId (wrong key, so it
  // resets to 0) and never assigns accountNumber or selectedRepCode at all, so
  // round-tripping through it would wipe the user's saved rep/account filters.
  setTransactionSortFromPreferences(field: string, dir: 'asc' | 'desc') {
    if (!field || !dir) {
      return
    }
    const current = this.getTransactionViewState()
    const sort = [{ field, dir }]
    this.setTransactionViewState(
      current
        ? ({ ...current, sort } as TransactionViewState)
        : new TransactionViewState({ sort }),
    )
  }

  clearViewStates() {
    this.setPendingTransactionViewState(null)
    this.setTransactionViewState(null)
    this.setTransactionWithPendingAlertsViewState(null)
    this.setAccountViewState(null)
  }
}
